import React from "react";
import { Phone, MapPin, Clock, FileText, ExternalLink } from "lucide-react";

const ContactSection = () => {
  return (
    <section id="contacto" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-sage-dark mb-6">
            Contacte-nos
          </h2>
          <p className="text-xl text-sage-medium max-w-3xl mx-auto leading-relaxed">
            Estamos aqui para tornar a sua experiência única. Entre em contacto connosco 
            para mais informações sobre os nossos tratamentos e serviços.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-playfair font-bold text-sage-dark mb-6 text-center">
                Informações de Contacto
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-gold-warm/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-gold-warm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sage-dark mb-1">Telefone</h4>
                    <a href="tel:253279717" className="text-sage-medium hover:text-gold-warm transition-colors text-lg">
                      253 279 717
                    </a>
                    <p className="text-sm text-sage-medium opacity-80 mt-1">Para marcações e informações</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-sage-soft/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-sage-dark" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sage-dark mb-1">Morada</h4>
                    <p className="text-sage-medium">
                      R. de Santo André 51<br />
                      4710-309 Braga
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-cream-warm/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-cream-warm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sage-dark mb-1">Horário</h4>
                    <div className="text-sage-medium space-y-1">
                      <p>Segunda-feira: <span className="text-red-500">Encerrado</span></p>
                      <p>Terça a Sexta: 09:30 - 19:00</p>
                      <p>Sábado: 09:30 - 19:00</p>
                      <p>Domingo: <span className="text-red-500">Encerrado</span></p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-red-500/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sage-dark mb-1">Livro de Reclamações</h4>
                    <a 
                      href="https://www.livroreclamacoes.pt/Inicio/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sage-medium hover:text-red-500 transition-colors flex items-center space-x-1"
                    >
                      <span>Portal das Reclamações</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <p className="text-sm text-sage-medium opacity-80">Acesso online disponível</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Map */}
            <div className="bg-gray-100 rounded-2xl overflow-hidden h-80">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3016.7996728537676!2d-8.424422484651218!3d41.55368397924507!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd24feea4980bb37%3A0xbb64af5625210be0!2sR.%20de%20Santo%20Andr%C3%A9%2051%2C%204710-309%20Braga%2C%20Portugal!5e0!3m2!1sen!2spt!4v1641234567890!5m2!1sen!2spt"
                width="100%" 
                height="100%" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização da Fonte de Juvence"
              ></iframe>
            </div>

            {/* Legal Info & Social Media */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-sage-soft/10 rounded-2xl p-6">
                <h4 className="font-semibold text-sage-dark mb-3">Dados da Empresa</h4>
                <div className="text-sage-medium space-y-1">
                  <p><strong>Fonte de Juvence, Lda</strong></p>
                  <p>NIPC: 508 627 257</p>
                  <p>R. de Santo André 51, 4710-309 Braga</p>
                </div>
              </div>

              <div className="bg-gold-warm/10 rounded-2xl p-6">
                <h4 className="font-semibold text-sage-dark mb-3">Siga-nos</h4>
                <div className="flex space-x-4">
                  <a 
                    href="https://www.instagram.com/fontedejuvence/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sage-medium hover:text-gold-warm transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span>📱</span>
                    <span>Instagram</span>
                  </a>
                  <a 
                    href="https://www.facebook.com/p/FONTE-DE-JUVENCE-100063727261156/?locale=pt_PT" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sage-medium hover:text-gold-warm transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span>👍</span>
                    <span>Facebook</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center bg-cream-light rounded-3xl p-8">
              <h3 className="text-2xl font-playfair font-bold text-sage-dark mb-4">
                Pronta para se sentir radiante?
              </h3>
              <p className="text-sage-medium mb-6 leading-relaxed max-w-2xl mx-auto">
                A nossa equipa especializada está pronta para criar a experiência perfeita para si. 
                Ligue-nos hoje mesmo para mais informações ou para marcar a sua consulta.
              </p>
              <a 
                href="tel:253279717"
                className="inline-flex items-center space-x-2 bg-gold-warm text-white px-8 py-4 rounded-full hover:bg-gold-deep transition-all duration-300 transform hover:scale-105 font-semibold"
              >
                <Phone className="w-5 h-5" />
                <span>Ligar Agora</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;